import React, { useCallback, useEffect, useState } from "react";
import { KeyRound, Copy, Check, ShieldCheck, AlertTriangle, Trash2, Save, Webhook } from "lucide-react";

const MODE_COPY = {
  platform: { tone: "var(--accent)", label: "Emergent test key", note: "Donations run in TEST mode — no real money is collected. Add your own Stripe secret key below to start accepting real donations." },
  test: { tone: "var(--accent)", label: "Your Stripe test key", note: "You're using your own Stripe key in TEST mode. Swap in a live key (sk_live_…) when you're ready to collect real donations." },
  live: { tone: "var(--accent)", label: "Your live Stripe key", note: "You're collecting real donations through your own Stripe account." },
  unknown: { tone: "var(--muted)", label: "Custom key", note: "A custom Stripe key is configured." },
  none: { tone: "var(--destructive)", label: "No key configured", note: "No Stripe key is set — donations cannot be processed until you add one." },
};

export const AdminStripeSettings = ({ authedClient }) => {
  const [data, setData] = useState(null);
  const [secret, setSecret] = useState("");
  const [webhook, setWebhook] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await authedClient.get("/admin/settings/stripe");
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load Stripe settings.");
    }
  }, [authedClient]);

  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 2200); };

  const save = async () => {
    setError("");
    if (!secret.trim() && !webhook.trim()) {
      setError("Enter a secret key and/or webhook signing secret to save.");
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      if (secret.trim()) payload.secret_key = secret.trim();
      if (webhook.trim()) payload.webhook_secret = webhook.trim();
      const res = await authedClient.post("/admin/settings/stripe", payload);
      setData(res.data);
      setSecret("");
      setWebhook("");
      flash("Stripe settings saved.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  const clearKey = async () => {
    if (!window.confirm("Remove your Stripe key and revert to the platform test key? Real donations will stop until you add a key again.")) return;
    setSaving(true);
    setError("");
    try {
      const res = await authedClient.post("/admin/settings/stripe", { clear_secret_key: true, clear_webhook_secret: true });
      setData(res.data);
      flash("Reverted to the platform test key.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not clear settings.");
    } finally {
      setSaving(false);
    }
  };

  const copyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(data?.webhook_url || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* noop */ }
  };

  if (!data) {
    return <p style={{ color: "hsl(var(--muted))" }} data-testid="stripe-settings-loading">Loading Stripe settings…</p>;
  }

  const mode = MODE_COPY[data.active_mode] || MODE_COPY.unknown;
  const isLive = data.active_mode === "live";

  return (
    <div data-testid="admin-stripe-settings" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 0.9fr)", gap: "1.5rem", alignItems: "start" }} className="stripe-grid">
      {/* Key management */}
      <section style={{ border: "1px solid hsl(var(--line))", background: "hsl(var(--bg-elev))", padding: "1.5rem" }}>
        <h3 style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 0, fontSize: "1.2rem" }}>
          <KeyRound size={17} aria-hidden style={{ color: "hsl(var(--accent))" }} /> Your Stripe keys
        </h3>

        <div data-testid="stripe-status" style={{
          display: "flex", gap: 10, alignItems: "flex-start",
          padding: "0.85rem 1rem", margin: "0 0 1.25rem",
          border: `1px solid hsl(${mode.tone} / 0.4)`,
          background: `hsl(${mode.tone} / 0.08)`,
        }}>
          {isLive ? <ShieldCheck size={18} aria-hidden style={{ color: `hsl(${mode.tone})`, marginTop: 1, flexShrink: 0 }} />
                  : <AlertTriangle size={18} aria-hidden style={{ color: `hsl(${mode.tone})`, marginTop: 1, flexShrink: 0 }} />}
          <div style={{ fontSize: "0.88rem", lineHeight: 1.5 }}>
            <strong style={{ color: "hsl(var(--ink))" }}>
              {mode.label}{data.secret_key_last4 ? ` (${data.secret_key_last4})` : ""}
            </strong>
            <div style={{ color: "hsl(var(--ink-soft))", marginTop: 2 }}>{mode.note}</div>
          </div>
        </div>

        <Field label="Stripe secret key">
          <input type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)}
            placeholder={data.using_own_key ? "Enter a new key to replace the current one" : "sk_live_…  (or sk_test_…)"}
            style={inputStyle} data-testid="stripe-secret-key-input" />
          <Hint>Stripe Dashboard → Developers → API keys → <strong>Secret key</strong>. Stored securely; shown only as the last 4 digits.</Hint>
        </Field>

        <Field label="Webhook signing secret">
          <input type="password" autoComplete="off" value={webhook} onChange={(e) => setWebhook(e.target.value)}
            placeholder={data.webhook_secret_set ? "Saved — enter a new value to replace" : "whsec_…"}
            style={inputStyle} data-testid="stripe-webhook-secret-input" />
          <Hint>From the webhook endpoint you create in Stripe (see the panel on the right).</Hint>
        </Field>

        {error && <Banner kind="error" testid="stripe-error">{error}</Banner>}
        {msg && <Banner kind="success" testid="stripe-msg">{msg}</Banner>}

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: "1rem" }}>
          <button type="button" className="btn btn--primary" onClick={save} disabled={saving} data-testid="stripe-save-btn">
            <Save size={15} aria-hidden /> {saving ? "Saving…" : "Save keys"}
          </button>
          {data.using_own_key && (
            <button type="button" className="btn btn--ghost" onClick={clearKey} disabled={saving} data-testid="stripe-clear-btn"
              style={{ borderColor: "hsl(var(--destructive) / 0.4)", color: "hsl(var(--destructive))" }}>
              <Trash2 size={14} aria-hidden /> Remove key
            </button>
          )}
        </div>
      </section>

      {/* Webhook setup */}
      <section style={{ border: "1px solid hsl(var(--line))", background: "hsl(var(--bg-elev))", padding: "1.5rem" }}>
        <h3 style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 0, fontSize: "1.2rem" }}>
          <Webhook size={17} aria-hidden style={{ color: "hsl(var(--accent))" }} /> Webhook setup
        </h3>
        <p style={{ fontSize: "0.9rem", lineHeight: 1.55, color: "hsl(var(--ink-soft))", marginTop: 0 }}>
          In Stripe → <strong>Developers → Webhooks → Add endpoint</strong>, paste this URL and select the events below.
          Then copy the endpoint’s <em>Signing secret</em> into the field on the left.
        </p>

        <Field label="Webhook endpoint URL">
          <div style={{ display: "flex", gap: 8 }}>
            <input type="text" readOnly value={data.webhook_url} data-testid="stripe-webhook-url"
              style={{ ...inputStyle, fontFamily: "var(--font-body)", fontSize: "0.82rem" }} onFocus={(e) => e.target.select()} />
            <button type="button" onClick={copyWebhook} data-testid="stripe-copy-webhook" className="btn btn--ghost" style={{ padding: "0 0.9rem" }}>
              {copied ? <Check size={15} aria-hidden /> : <Copy size={15} aria-hidden />}
            </button>
          </div>
        </Field>

        <div style={{ marginTop: "1rem" }}>
          <Label>Events to send</Label>
          <ul style={{ margin: "8px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
            {(data.recommended_events || []).map((ev) => (
              <li key={ev} style={{
                fontFamily: "var(--font-body)", fontSize: "0.82rem",
                color: "hsl(var(--ink-soft))",
                background: "hsl(var(--bg))", border: "1px solid hsl(var(--line))",
                padding: "0.4rem 0.6rem", borderRadius: 2,
              }}>{ev}</li>
            ))}
          </ul>
        </div>

        <p style={{ fontSize: "0.8rem", color: "hsl(var(--muted))", marginTop: "1rem", lineHeight: 1.5 }}>
          Without a signing secret, donations are still confirmed by the site (we poll Stripe after each gift) — the webhook
          just adds real-time confirmation and is recommended for live mode.
        </p>
      </section>

      <style>{`
        @media (max-width: 860px) { .stripe-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: "1rem" }}>
    <Label>{label}</Label>
    <div style={{ marginTop: 6 }}>{children}</div>
  </div>
);

const Label = ({ children }) => (
  <span style={{ fontSize: "0.74rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "hsl(var(--muted))", fontWeight: 600 }}>{children}</span>
);

const Hint = ({ children }) => (
  <p style={{ fontSize: "0.78rem", color: "hsl(var(--muted))", margin: "6px 0 0", lineHeight: 1.45 }}>{children}</p>
);

const Banner = ({ kind, children, testid }) => (
  <div role={kind === "error" ? "alert" : "status"} data-testid={testid} style={{
    marginTop: "1rem", padding: "0.65rem 0.85rem", fontSize: "0.88rem",
    border: `1px solid hsl(var(--${kind === "error" ? "destructive" : "accent"}) / 0.5)`,
    background: `hsl(var(--${kind === "error" ? "destructive" : "accent"}) / 0.08)`,
    color: `hsl(var(--${kind === "error" ? "destructive" : "accent"}))`,
  }}>{children}</div>
);

const inputStyle = {
  width: "100%",
  padding: "0.7rem 0.85rem",
  border: "1px solid hsl(var(--line))",
  background: "hsl(var(--bg))",
  color: "hsl(var(--ink))",
  fontSize: "0.95rem",
  fontFamily: "var(--font-body)",
  borderRadius: 2,
};
